// Pre-visit intake catalog — ported from Luan's intake-forms.html (12 chief-complaint categories,
// each with its own short questionnaire). Drives booking step 01. This is a PRESENTATION-LAYER catalog
// of question/option strings.
//
// ⚠️ COMPLIANCE: the appointment still stores only the coarse, non-clinical `bucket` as `label` (D5).
// The granular ANSWERS the member gives are persisted separately, AES-256-GCM encrypted + RLS-scoped,
// to the FB-authorized demo store (demo_visit_intake, migration 0012) — a deliberate carve-out (D5.1)
// so the provider can recall them on /patients/:id. This file is allowlisted in
// scripts/check-clinical-terms.mjs (it names symptoms/conditions in option labels). Do not widen that
// allowlist without the same review.
import type { AppointmentLabel } from "@forcebase-ai/core";

// Top-level care category. Coarse + non-clinical: it groups the reason grid and is persisted (with the
// intake answers) so the provider can tell a dental/vision visit apart from a medical one.
export type VisitCategory = "medical" | "dental" | "vision";

export type IntakeField =
  | { t: "multi"; q: string; opts: string[] }
  | { t: "single"; q: string; opts: string[]; flag?: boolean }
  | { t: "text"; q: string; ph?: string }
  | { t: "area"; q: string; ph?: string }
  | { t: "note"; msg: string };

export interface IntakeForm {
  key: string; // stable, namespaced reason key (e.g. "cold", "dental.tooth_pain") — persisted as form_key
  category: VisitCategory;
  title: string;
  sub: string; // selector subtitle
  est: string;
  bucket: AppointmentLabel; // the ONLY value that reaches the backend as the appointment `label` (D5)
  icon: string; // inner SVG markup (static, app-authored)
  fields: IntakeField[];
}

// Category metadata for the step-01 selector (segmented control). Order = display order.
export const VISIT_CATEGORIES: { key: VisitCategory; title: string; icon: string }[] = [
  { key: "medical", title: "Medical", icon: "stethoscope" },
  { key: "dental", title: "Dental", icon: "tooth" },
  { key: "vision", title: "Vision", icon: "eye" },
];

export const CATEGORY_LABEL: Record<VisitCategory, string> = {
  medical: "Medical",
  dental: "Dental",
  vision: "Vision",
};

/** Forms for a category, in catalog order. */
export const formsForCategory = (c: VisitCategory): IntakeForm[] =>
  INTAKE_FORMS.filter((f) => f.category === c);

const multi = (q: string, opts: string[]): IntakeField => ({ t: "multi", q, opts });
const single = (q: string, opts: string[]): IntakeField => ({ t: "single", q, opts });
const yesno = (q: string, flag = false): IntakeField => ({ t: "single", q, opts: ["No", "Yes"], flag });
const text = (q: string, ph?: string): IntakeField => ({ t: "text", q, ph });
const area = (q: string, ph?: string): IntakeField => ({ t: "area", q, ph });
const note = (msg: string): IntakeField => ({ t: "note", msg });

// Line icons (inner markup), matched to categories — ported verbatim from the prototype.
export const INTAKE_ICONS: Record<string, string> = {
  lungs: '<path d="M12 4v8"/><path d="M12 12c0 4-1 6-3.5 7.5C6 21 5 19 5 16v-3c0-2 1-3 3-3 1.5 0 2 .8 2 2"/><path d="M12 12c0 4 1 6 3.5 7.5C18 21 19 19 19 16v-3c0-2-1-3-3-3-1.5 0-2 .8-2 2"/>',
  allergy: '<circle cx="12" cy="12" r="3"/><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1"/>',
  drop: '<path d="M12 3s6 7 6 11a6 6 0 0 1-12 0c0-4 6-11 6-11z"/>',
  skin: '<rect x="4" y="4" width="16" height="16" rx="5"/><circle cx="9.5" cy="10" r="1" fill="currentColor"/><circle cx="14.5" cy="10" r="1" fill="currentColor"/><circle cx="12" cy="14.5" r="1" fill="currentColor"/>',
  bone: '<line x1="8.6" y1="15.4" x2="15.4" y2="8.6"/><circle cx="6.8" cy="15.8" r="1.7"/><circle cx="8.2" cy="17.2" r="1.7"/><circle cx="17.2" cy="8.2" r="1.7"/><circle cx="15.8" cy="6.8" r="1.7"/>',
  stomach: '<circle cx="12" cy="12" r="9"/><path d="M8.5 14.5a4 4 0 0 0 7 0"/><circle cx="9" cy="10" r=".6" fill="currentColor"/><circle cx="15" cy="10" r=".6" fill="currentColor"/>',
  heart: '<path d="M12 20s-7-4.6-7-9.5A3.5 3.5 0 0 1 12 7a3.5 3.5 0 0 1 7 3.5C19 15.4 12 20 12 20z"/>',
  pill: '<rect x="3" y="9" width="18" height="6" rx="3" transform="rotate(45 12 12)"/><path d="M12 6.5L17.5 12" transform="rotate(45 12 12)"/>',
  flask: '<path d="M9 3h6M10 3v6l-5 8.5A2 2 0 0 0 6.7 21h10.6a2 2 0 0 0 1.7-3.5L14 9V3"/>',
  wellness: '<circle cx="12" cy="12" r="9"/><path d="M8.5 12.5l2.5 2.5 4.5-5"/>',
  chat: '<path d="M21 12a8 8 0 0 1-11.5 7.2L4 21l1.8-5.5A8 8 0 1 1 21 12z"/>',
  dots: '<circle cx="12" cy="12" r="9"/><circle cx="8.5" cy="12" r="1" fill="currentColor"/><circle cx="12" cy="12" r="1" fill="currentColor"/><circle cx="15.5" cy="12" r="1" fill="currentColor"/>',
  // Category + dental/vision reason icons (ported verbatim from the prototype).
  stethoscope: '<path d="M6 3H5v4a4 4 0 0 0 8 0V3h-1"/><path d="M9 15a5 5 0 0 0 5 5 4 4 0 0 0 4-4v-1.5"/><circle cx="18.5" cy="13" r="2"/>',
  tooth: '<path d="M8 3.5C6 3.5 4.5 5 4.5 7.2c0 1.3.4 2.2.6 3.6.3 2.2.3 4.4 1 6.6.4 1.3.7 2.6 1.4 2.6.8 0 .8-1.6 1-3 .2-1.2.4-2 1.5-2s1.3.8 1.5 2c.2 1.4.2 3 1 3 .7 0 1-1.3 1.4-2.6.7-2.2.7-4.4 1-6.6.2-1.4.6-2.3.6-3.6C16.5 5 15 3.5 13 3.5c-1.1 0-1.7.5-2.5.5S9.1 3.5 8 3.5z"/>',
  toothCrack: '<path d="M8 3.5C6 3.5 4.5 5 4.5 7.2c0 1.3.4 2.2.6 3.6.3 2.2.3 4.4 1 6.6.4 1.3.7 2.6 1.4 2.6.8 0 .8-1.6 1-3 .2-1.2.4-2 1.5-2s1.3.8 1.5 2c.2 1.4.2 3 1 3 .7 0 1-1.3 1.4-2.6.7-2.2.7-4.4 1-6.6.2-1.4.6-2.3.6-3.6C16.5 5 15 3.5 13 3.5c-1.1 0-1.7.5-2.5.5S9.1 3.5 8 3.5z"/><path d="M11 6l-1.3 3.2H12l-1.4 3.3"/>',
  mouth: '<path d="M5 11c1.6-1.6 3.6-2.2 7-2.2s5.4.6 7 2.2c-1.6 2.6-4 3.8-7 3.8s-5.4-1.2-7-3.8z"/><path d="M5 11h14"/>',
  sparkle: '<path d="M12 3l1.7 4.8L18.5 9.5l-4.8 1.7L12 16l-1.7-4.8L5.5 9.5l4.8-1.7z"/><path d="M18 15l.6 1.7 1.7.6-1.7.6L18 20l-.6-1.5-1.7-.6 1.7-.6z"/>',
  eye: '<path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/>',
  glasses: '<circle cx="6.5" cy="13" r="3.3"/><circle cx="17.5" cy="13" r="3.3"/><path d="M9.8 13c.6-1 3.8-1 4.4 0"/><path d="M3.2 11l1.8-3.2M20.8 11l-1.8-3.2"/>',
};

const OPTIONAL = "Optional";

export const INTAKE_FORMS: IntakeForm[] = [
  {
    key: "cold", category: "medical", title: "Cold, flu & respiratory", sub: "Cough, congestion, sore throat, or fever",
    est: "About 2 minutes", bucket: "general", icon: INTAKE_ICONS.lungs!,
    fields: [
      multi("Which symptoms are you having?", ["Cough", "Congestion", "Sore throat", "Fever or chills", "Body aches", "Runny nose", "Shortness of breath", "Loss of taste or smell"]),
      single("How long have you had symptoms?", ["Today", "1–3 days", "4–7 days", "Over a week"]),
      single("How bad does it feel overall?", ["Mild", "Moderate", "Severe"]),
      yesno("Any trouble breathing or chest pain?", true),
      text("What have you tried so far?", "e.g. rest, fluids, ibuprofen, cough syrup"),
      area("Anything else you'd like your provider to know?", OPTIONAL),
    ],
  },
  {
    key: "sinus", category: "medical", title: "Sinus & allergies", sub: "Sinus pressure, congestion, or seasonal allergies",
    est: "About 2 minutes", bucket: "general", icon: INTAKE_ICONS.allergy!,
    fields: [
      multi("Which symptoms are you having?", ["Facial pressure or pain", "Nasal congestion", "Runny nose", "Post-nasal drip", "Sneezing", "Itchy or watery eyes", "Reduced sense of smell"]),
      single("How long has it been going on?", ["A few days", "About a week", "1–2 weeks", "Longer / comes and goes"]),
      yesno("Do these symptoms come back with the seasons?"),
      yesno("Thick discolored discharge or worsening facial pain?", true),
      text("Any allergy or sinus meds you've tried?", "e.g. Claritin, Flonase, Sudafed"),
      area("Anything else you'd like your provider to know?", OPTIONAL),
    ],
  },
  {
    key: "uti", category: "medical", title: "Urinary", sub: "Burning, urgency, or other urinary symptoms",
    est: "Under 2 minutes", bucket: "general", icon: INTAKE_ICONS.drop!,
    fields: [
      multi("What are you noticing?", ["Burning when you urinate", "Frequent urge to go", "Cloudy or strong-smelling urine", "Blood in urine", "Lower belly or pelvic pain"]),
      single("How long has it been going on?", ["Today", "1–2 days", "3+ days"]),
      yesno("Any fever, chills, or pain in your back or side?", true),
      yesno("Have you had a UTI before?"),
      single("Could you be pregnant?", ["No", "Yes", "Possibly", "Not applicable"]),
      area("Anything else you'd like your provider to know?", OPTIONAL),
    ],
  },
  {
    key: "skin", category: "medical", title: "Skin & rash", sub: "A rash, irritation, or other skin concern",
    est: "About 2 minutes", bucket: "general", icon: INTAKE_ICONS.skin!,
    fields: [
      text("Where on your body is it?", "e.g. left forearm, both cheeks, back"),
      single("How long have you had it?", ["Today", "A few days", "About a week", "Longer"]),
      multi("How does it look or feel?", ["Red", "Itchy", "Painful", "Blistering", "Scaly or flaky", "Swelling", "Spreading"]),
      text("Anything new recently?", "New product, food, medication, plant, or bite"),
      yesno("Any fever or feeling generally unwell?", true),
      note("A clear, well-lit photo helps your provider a lot — you'll be able to add one on the next screen."),
      area("Anything else you'd like your provider to know?", OPTIONAL),
    ],
  },
  {
    key: "msk", category: "medical", title: "Musculoskeletal", sub: "Joint, muscle, or back pain",
    est: "About 2 minutes", bucket: "general", icon: INTAKE_ICONS.bone!,
    fields: [
      multi("Where is the pain or problem?", ["Neck", "Shoulder", "Back", "Elbow", "Hand or wrist", "Hip", "Knee", "Ankle or foot"]),
      single("How did it start?", ["Sudden injury", "Came on gradually", "After training or activity", "Not sure"]),
      single("How long has it been going on?", ["Today", "A few days", "A couple weeks", "Longer"]),
      multi("What are you noticing?", ["Sharp pain", "Dull ache", "Stiffness", "Swelling", "Weakness", "Limited range of motion", "Numbness or tingling", "Clicking or popping"]),
      single("How bad is it?", ["Mild", "Moderate", "Severe"]),
      yesno("Can't put weight on it, obvious deformity, or numbness that's spreading?", true),
      text("What helps, or what have you tried?", "e.g. rest, ice, a brace, ibuprofen"),
      area("Anything else you'd like your provider to know?", OPTIONAL),
    ],
  },
  {
    key: "stomach", category: "medical", title: "Stomach & digestion", sub: "Nausea, pain, or digestive trouble",
    est: "About 2 minutes", bucket: "general", icon: INTAKE_ICONS.stomach!,
    fields: [
      multi("What are you experiencing?", ["Nausea", "Vomiting", "Diarrhea", "Constipation", "Stomach pain", "Bloating", "Heartburn"]),
      single("How long has it been going on?", ["Today", "1–3 days", "About a week", "Longer"]),
      text("Where is the pain, if any?", "e.g. upper belly, lower right side"),
      yesno("Severe pain, blood in stool, black stools, or can't keep fluids down?", true),
      text("Any new foods, recent travel, or diet changes?", OPTIONAL),
      area("Anything else you'd like your provider to know?", OPTIONAL),
    ],
  },
  {
    key: "anxiety", category: "medical", title: "Anxiety & mood", sub: "Stress, anxiety, low mood, or sleep",
    est: "About 2 minutes", bucket: "general", icon: INTAKE_ICONS.heart!,
    fields: [
      multi("What have you been experiencing?", ["Anxiety or worry", "Low mood", "Stress", "Trouble sleeping", "Panic", "Irritability", "Low energy"]),
      single("How long has it been going on?", ["Under 2 weeks", "A few weeks", "A few months", "Longer"]),
      single("How much is it affecting your daily life?", ["A little", "Somewhat", "A lot"]),
      yesno("Currently taking any medication for mood or mental health?"),
      yesno("In the past two weeks, have you had thoughts of harming yourself?"),
      note("If you're in crisis, you don't have to wait for your appointment — call or text 988 (the Suicide & Crisis Lifeline) anytime, day or night."),
      area("Anything you'd like your provider to know before you talk?", OPTIONAL),
    ],
  },
  {
    key: "refill", category: "medical", title: "Prescription refill", sub: "Renew an existing medication",
    est: "Under 2 minutes", bucket: "follow_up", icon: INTAKE_ICONS.pill!,
    fields: [
      text("Which medication(s) do you need refilled?", "e.g. Lisinopril 10mg"),
      text("Dose and how often you take it", "e.g. once daily in the morning"),
      single("When did you last fill it?", ["This week", "1–2 weeks ago", "3–4 weeks ago", "Over a month ago"]),
      text("Preferred pharmacy", "Name & location"),
      yesno("Any new side effects or changes since you started it?"),
      yesno("Are you still seeing the provider who prescribed it?"),
      area("Anything else you'd like your provider to know?", OPTIONAL),
    ],
  },
  {
    key: "lab", category: "medical", title: "Lab test review", sub: "Go over recent lab or test results",
    est: "Under 2 minutes", bucket: "general", icon: INTAKE_ICONS.flask!,
    fields: [
      text("Which test(s) would you like to review?", "e.g. cholesterol panel, thyroid, A1C"),
      text("Where and roughly when were they done?", "e.g. Quest, last week"),
      single("Do we have your results?", ["I already have them", "I'll upload them", "Please request from the lab", "Not sure"]),
      area("What questions or concerns do you have about the results?", OPTIONAL),
    ],
  },
  {
    key: "wellness", category: "medical", title: "Annual wellness", sub: "A routine check-in or yearly visit",
    est: "About 3 minutes", bucket: "check_in", icon: INTAKE_ICONS.wellness!,
    fields: [
      area("Any new health concerns since your last visit?", OPTIONAL),
      text("Current medications and supplements", "List anything you take regularly"),
      multi("Anything new since last time?", ["New diagnosis", "New medication", "Surgery or hospital stay", "New condition in a close relative", "None"]),
      text("Any screenings or vaccines you think you're due for?", OPTIONAL),
      area("What would you like to focus on in this visit?", OPTIONAL),
    ],
  },
  {
    key: "general", category: "medical", title: "General consultation", sub: "A new symptom, concern, or question",
    est: "About 2 minutes", bucket: "new_visit", icon: INTAKE_ICONS.chat!,
    fields: [
      area("What's the main thing you'd like to talk about?", "Describe it in your own words"),
      single("How long has this been going on?", ["Today", "A few days", "A couple weeks", "Longer"]),
      single("How would you rate it?", ["Mild", "Moderate", "Severe"]),
      area("What are you hoping to get out of this visit?", OPTIONAL),
    ],
  },
  {
    key: "other", category: "medical", title: "Something else", sub: "Tell your doctor what's on your mind",
    est: "About 2 minutes", bucket: "general", icon: INTAKE_ICONS.dots!,
    fields: [
      area("Tell your provider what's on your mind", "There are no wrong answers here"),
      single("How long has this been going on?", ["Just now / today", "A few days", "A few weeks", "Longer"]),
      single("How urgent does it feel to you?", ["Not urgent", "Somewhat", "Pretty urgent"]),
      area("Anything else that would help us prepare?", OPTIONAL),
    ],
  },

  /* ================= DENTAL ================= */
  {
    key: "dental.tooth_pain", category: "dental", title: "Tooth pain", sub: "Aching, throbbing, or sensitive tooth",
    est: "About 2 minutes", bucket: "general", icon: INTAKE_ICONS.tooth!,
    fields: [
      text("Which tooth or area?", "e.g. lower right molar"),
      single("How long has it hurt?", ["Today", "A few days", "About a week", "Longer"]),
      multi("What does it feel like?", ["Throbbing", "Sharp when biting", "Constant ache", "Sensitive to hot or cold", "Worse at night"]),
      single("How bad is it?", ["Mild", "Moderate", "Severe"]),
      yesno("Any facial swelling, fever, or trouble swallowing?", true),
      text("What have you tried?", "e.g. ibuprofen, salt rinse, numbing gel"),
      area("Anything else you'd like your dentist to know?", OPTIONAL),
    ],
  },
  {
    key: "dental.broken_tooth", category: "dental", title: "Broken or chipped tooth", sub: "Cracked, chipped, or knocked-out tooth",
    est: "About 2 minutes", bucket: "general", icon: INTAKE_ICONS.toothCrack!,
    fields: [
      single("What happened to the tooth?", ["Chipped", "Cracked", "Broke off", "Knocked out", "Feels loose"]),
      text("Which tooth?", "e.g. upper front"),
      single("When did it happen?", ["Today", "Yesterday", "A few days ago", "Longer"]),
      yesno("Is it painful right now?"),
      yesno("Bleeding that won't stop, or a tooth knocked out?", true),
      note("If a tooth was knocked out, keep it in milk or your saliva and seek care right away — time matters for saving it."),
      area("Anything else you'd like your dentist to know?", OPTIONAL),
    ],
  },
  {
    key: "dental.gum", category: "dental", title: "Gum problem", sub: "Bleeding, swollen, or sore gums",
    est: "About 2 minutes", bucket: "general", icon: INTAKE_ICONS.mouth!,
    fields: [
      multi("What are you noticing?", ["Bleeding gums", "Swollen gums", "Sore or tender", "Receding gums", "Bad breath", "Pus or discharge"]),
      single("How long has it been going on?", ["A few days", "About a week", "A couple weeks", "Longer"]),
      yesno("Do they bleed when you brush or floss?"),
      yesno("Any facial swelling or fever?", true),
      area("Anything else you'd like your dentist to know?", OPTIONAL),
    ],
  },
  {
    key: "dental.lost_filling", category: "dental", title: "Lost filling or crown", sub: "A filling, crown, or cap came off",
    est: "Under 2 minutes", bucket: "follow_up", icon: INTAKE_ICONS.tooth!,
    fields: [
      single("What came off?", ["Filling", "Crown or cap", "Veneer", "Bridge", "Not sure"]),
      text("Which tooth?", "e.g. lower left"),
      single("When did it come off?", ["Today", "A few days ago", "About a week ago", "Longer"]),
      yesno("Is the tooth painful or sensitive now?"),
      yesno("Do you still have the piece that came off?"),
      area("Anything else you'd like your dentist to know?", OPTIONAL),
    ],
  },
  {
    key: "dental.mouth_sore", category: "dental", title: "Mouth sore", sub: "Ulcer, blister, or sore in the mouth",
    est: "Under 2 minutes", bucket: "general", icon: INTAKE_ICONS.mouth!,
    fields: [
      text("Where in your mouth is it?", "e.g. inside lower lip, side of tongue"),
      single("How long has it been there?", ["A few days", "About a week", "2+ weeks", "Comes and goes"]),
      multi("What is it like?", ["Painful", "Burning", "White or yellow", "Red", "Bleeding"]),
      yesno("Has it lasted over two weeks or kept coming back?", true),
      area("Anything else you'd like your dentist to know?", OPTIONAL),
    ],
  },
  {
    key: "dental.sensitivity", category: "dental", title: "Tooth sensitivity", sub: "Pain with hot, cold, or sweet",
    est: "Under 2 minutes", bucket: "general", icon: INTAKE_ICONS.tooth!,
    fields: [
      multi("Sensitive to what?", ["Cold", "Hot", "Sweet", "Biting or pressure", "Air"]),
      text("Which tooth or area?", "e.g. upper right"),
      single("How long has it been going on?", ["Recently", "A few weeks", "Longer"]),
      single("How bad is it?", ["Mild", "Moderate", "Severe"]),
      area("Anything else you'd like your dentist to know?", OPTIONAL),
    ],
  },
  {
    key: "dental.checkup", category: "dental", title: "Cleaning or checkup", sub: "Routine cleaning or exam",
    est: "Under 2 minutes", bucket: "check_in", icon: INTAKE_ICONS.sparkle!,
    fields: [
      single("When was your last dental visit?", ["Within 6 months", "6–12 months", "1–2 years", "Over 2 years / not sure"]),
      multi("What would you like done?", ["Routine cleaning", "General check-up", "Whitening questions", "Alignment questions", "Nothing specific"]),
      text("Any current dental concerns?", OPTIONAL),
      area("Anything else you'd like your dentist to know?", OPTIONAL),
    ],
  },
  {
    key: "dental.other", category: "dental", title: "Something else", sub: "Tell your dentist what's on your mind",
    est: "About 2 minutes", bucket: "general", icon: INTAKE_ICONS.dots!,
    fields: [
      area("Tell your dentist what's going on", "There are no wrong answers here"),
      single("How long has this been going on?", ["Just now / today", "A few days", "A few weeks", "Longer"]),
      single("How urgent does it feel to you?", ["Not urgent", "Somewhat", "Pretty urgent"]),
      area("Anything else that would help us prepare?", OPTIONAL),
    ],
  },

  /* ================= VISION ================= */
  {
    key: "vision.red_eye", category: "vision", title: "Red or irritated eye", sub: "Redness, itching, or discharge",
    est: "About 2 minutes", bucket: "general", icon: INTAKE_ICONS.eye!,
    fields: [
      single("Which eye?", ["Left", "Right", "Both"]),
      multi("What are you noticing?", ["Redness", "Itching", "Watering", "Discharge or crust", "Burning", "Gritty feeling"]),
      single("How long has it been going on?", ["Today", "1–2 days", "3+ days", "About a week"]),
      yesno("Any vision changes or sensitivity to light?", true),
      yesno("Do you wear contact lenses?"),
      text("Anything that may have triggered it?", "e.g. allergies, swimming, new product"),
      area("Anything else you'd like your eye doctor to know?", OPTIONAL),
    ],
  },
  {
    key: "vision.eye_pain", category: "vision", title: "Eye pain or injury", sub: "Pain, something in the eye, or injury",
    est: "About 2 minutes", bucket: "general", icon: INTAKE_ICONS.eye!,
    fields: [
      single("Which eye?", ["Left", "Right", "Both"]),
      single("What happened?", ["Something got in it", "Hit or scratched", "Came on its own", "Chemical splash"]),
      single("How long ago?", ["Just now", "Today", "A few days"]),
      multi("What are you feeling?", ["Sharp pain", "Aching", "Feels like something's in it", "Light sensitivity", "Tearing"]),
      yesno("Chemical splash, deep injury, or sudden vision loss?", true),
      note("For a chemical splash, rinse the eye with clean water for about 15 minutes and seek urgent care right away."),
      area("Anything else you'd like your eye doctor to know?", OPTIONAL),
    ],
  },
  {
    key: "vision.flashes", category: "vision", title: "Flashes or floaters", sub: "New flashes, floaters, or shadows",
    est: "About 2 minutes", bucket: "general", icon: INTAKE_ICONS.eye!,
    fields: [
      single("Which eye?", ["Left", "Right", "Both"]),
      multi("What are you seeing?", ["New floaters", "Flashes of light", "A shadow or curtain", "More floaters than usual"]),
      single("When did it start?", ["Today", "1–2 days ago", "This week", "Longer"]),
      yesno("A curtain or shadow over your vision, or a sudden shower of floaters?", true),
      note("New flashes with a shadow can signal a retinal issue — if that's happening, please seek same-day eye care."),
      area("Anything else you'd like your eye doctor to know?", OPTIONAL),
    ],
  },
  {
    key: "vision.blurry", category: "vision", title: "Blurry vision", sub: "Sudden or worsening blurred vision",
    est: "About 2 minutes", bucket: "general", icon: INTAKE_ICONS.eye!,
    fields: [
      single("Which eye?", ["Left", "Right", "Both"]),
      single("How did it come on?", ["Suddenly", "Gradually", "Comes and goes"]),
      single("How long has it been going on?", ["Today", "A few days", "A couple weeks", "Longer"]),
      multi("Anything with it?", ["Headache", "Eye strain", "Double vision", "Hard to see far away", "Hard to read up close"]),
      yesno("Sudden vision loss, double vision, or with weakness or slurred speech?", true),
      area("Anything else you'd like your eye doctor to know?", OPTIONAL),
    ],
  },
  {
    key: "vision.dry_eye", category: "vision", title: "Dry or watery eyes", sub: "Dryness, grittiness, or watering",
    est: "Under 2 minutes", bucket: "general", icon: INTAKE_ICONS.eye!,
    fields: [
      multi("What are you noticing?", ["Dryness", "Grittiness", "Watering", "Burning", "Tired eyes", "Clears when you blink"]),
      single("How long has it been going on?", ["Recently", "A few weeks", "Longer / ongoing"]),
      text("Does anything make it worse?", "e.g. screens, wind, contacts"),
      yesno("Do you wear contact lenses?"),
      area("Anything else you'd like your eye doctor to know?", OPTIONAL),
    ],
  },
  {
    key: "vision.eyelid", category: "vision", title: "Eyelid or stye", sub: "A bump, swelling, or crusting on the lid",
    est: "Under 2 minutes", bucket: "general", icon: INTAKE_ICONS.eye!,
    fields: [
      single("Which eye?", ["Left", "Right", "Both"]),
      multi("What are you noticing?", ["Bump or lump", "Swelling", "Redness", "Crusting", "Tender to touch"]),
      single("How long has it been going on?", ["1–2 days", "A few days", "About a week", "Longer"]),
      yesno("Is the whole lid very swollen or hot, or is vision affected?", true),
      area("Anything else you'd like your eye doctor to know?", OPTIONAL),
    ],
  },
  {
    key: "vision.glasses", category: "vision", title: "Glasses or contacts", sub: "Renew or update a prescription",
    est: "Under 2 minutes", bucket: "follow_up", icon: INTAKE_ICONS.glasses!,
    fields: [
      single("What do you need?", ["Renew glasses prescription", "Renew contacts prescription", "New prescription", "Vision seems to have changed"]),
      single("When was your last eye exam?", ["Within a year", "1–2 years", "Over 2 years", "Not sure"]),
      text("Current prescription, if you have it", OPTIONAL),
      yesno("Noticing any new vision changes?"),
      area("Anything else you'd like your eye doctor to know?", OPTIONAL),
    ],
  },
  {
    key: "vision.other", category: "vision", title: "Something else", sub: "Tell your eye doctor what's on your mind",
    est: "About 2 minutes", bucket: "general", icon: INTAKE_ICONS.dots!,
    fields: [
      area("Tell your eye doctor what's going on", "There are no wrong answers here"),
      single("How long has this been going on?", ["Just now / today", "A few days", "A few weeks", "Longer"]),
      single("How urgent does it feel to you?", ["Not urgent", "Somewhat", "Pretty urgent"]),
      area("Anything else that would help us prepare?", OPTIONAL),
    ],
  },
];
